import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Form from '../../components/form'
import { Message } from '@paypalcorp/worldready-react'
// import cs from 'classnames';
import FormField from '../../components/form/formField'
import Heading from '../../components/heading'
import ImageCarousel from '../../components/imageCarousel'
import Section from '../../components/section'
import Button from '../../components/button'
import Divider from '../../components/divider'
import Glyph from '../../components/glyph'
import Loader from '../../components/loader'
import CheckoutModal from '../../components/checkoutModal'
import BulletPointsDescription from '../../components/bulletPointsDescription'
import { makeAPIRequest } from '../../utils/api'
import Alert from '../../components/alert'
import applicationData from '../../components/applicationContext/applicationData'
import { recordClick } from '../../utils/tracking'
import constants from '../../constants.js'
import { addZeroes } from '../../utils/number'
import serverData from '../../utils/server-data'

import styles from './checkout.module.css'
const domain = serverData.domain

const buynowButtonText = <Message id="checkout.buynow.button.text" />

const productDetailHeadingText = (
  <Message id="checkout.productDetails.section.heading.text" />
)

const shippingHeadingText = (countryLabel) => (
  <Message
    id="checkout.shipping.section.heading.text"
    countryLabel={countryLabel}
  />
)

const sellYourOwnText = <Message id="checkout.referral.sellYourOwn.text" />

const buyerShippingText = (countryLabel) => (
  <Message
    id="checkout.shipping.section.buyer.text"
    countryLabel={countryLabel}
  />
)

const errorTitleText = <Message id="checkout.error.title.text" />

const buyerDisclosure = (
  <Message
    link={(innerMessage) => (
      <a
        rel="noopener noreferrer"
        target="_blank"
        href={`https://www.${domain}/us/webapps/mpp/paypal-safety-and-security`}
      >
        {innerMessage}
      </a>
    )}
    id="checkout.buyerDisclosure.text"
  />
)

const reportListing = <Message id="checkout.reportListing.text" />

const pricedShipping = (currency, shippingCost) => {
  const cost = String(addZeroes(shippingCost))
  return (
    <Message
      id="checkout.pricedShipping.text"
      currency={currency}
      shippingCost={cost}
    />
  )
}

const freeShipping = <Message id="checkout.freeShipping.text" />

const madeWith = <Message id="checkout.footer.text" />

const getShippingInfoMessage = (shippingInfo, shippingCost, currency) => {
  switch (shippingInfo) {
    case '0':
      return [pricedShipping(currency, shippingCost)]

    default:
      return [freeShipping]
  }
}

const buttonVariablesMap = {
  item_name: 'productName',
  amount: 'productPrice',
  image_url: 'productImageURLs',
  no_shipping: 'shippingInfo',
  shipping: 'shippingCost',
  pp_note: 'productDescription',
  currency_code: 'currency',
}

const processButtonInfoResponse = (buttonInfoResponse = {}) => {
  const { button_variables: buttonVariables = [] } = buttonInfoResponse

  const product = {}

  for (
    let i = 0, buttonVariablesLen = buttonVariables.length;
    i < buttonVariablesLen;
    i++
  ) {
    const { name, value } = buttonVariables[i] || {}
    const mappedVariableName = buttonVariablesMap[name]
    if (mappedVariableName) {
      product[mappedVariableName] = value || ''
    }
  }

  // Update the Shipping Messages
  if (product.shippingInfo) {
    product.shippingInfo = getShippingInfoMessage(
      product.shippingInfo,
      product.shippingCost,
      product.currency,
    )
  }

  product.productCheckoutURL = buttonInfoResponse.email_code
  product.countryLabel = buttonInfoResponse.country_code
  return product
}

const Checkout = ({ match, ...rest }) => {
  const { buttonId } = match.params
  const [product, setProduct] = useState({})
  const [loading, setLoading] = useState(true)
  const [errorMessageId, setErrorMessageId] = useState('')
  const [showBuyNow, setShowBuyNow] = useState(false)
  useEffect(() => {
    async function getButtonInfo() {
      setLoading(true)
      setErrorMessageId('')
      try {
        const response =
          (await makeAPIRequest({
            url: `/instantcommerce/api/v1/products/${buttonId}`,
            method: 'GET',
          })) || {}
        setProduct(processButtonInfoResponse(response))
      } catch (e) {
        const { responseBody } = e || {}
        if (responseBody && responseBody.type) {
          setErrorMessageId(responseBody.type)
        } else {
          setErrorMessageId('UNKNOWN_TYPE')
        }
      } finally {
        setLoading(false)
      }
    }
    getButtonInfo()
  }, [buttonId])

  return (
    <>
      {errorMessageId ? (
        <FormField>
          <Alert type="error">
            <strong>{errorTitleText}: </strong>
            <Message id={`checkout.error.${errorMessageId}.text`} />
          </Alert>
        </FormField>
      ) : (
        <>
          <CheckoutModal
            isOpen={showBuyNow}
            onClose={() => setShowBuyNow((buyNowState) => !buyNowState)}
            product={product}
          />

          <Loader show={loading} priority={10} type="fixed" />
          {product && product.productName && (
            <Form>
              <ImageCarousel
                viewMode={true}
                previewURLs={product.productImageURLs || []}
              />
              <Heading
                text={product.productName}
                bold={true}
                Element="h2"
                centerAlign={true}
              />
              <Heading
                text={`${product.currency} ${product.productPrice}`}
                Element="h3"
                centerAlign={true}
              />
              <FormField centerContents={true}>
                <Button
                  size="lg"
                  block={true}
                  onClick={() => {
                    setShowBuyNow((buyNowState) => !buyNowState)
                    recordClick({
                      link: constants.fpti.link.buyNow,
                      item: buttonId,
                      ...rest,
                    })
                  }}
                  className={styles.buyButton}
                >
                  {buynowButtonText}
                </Button>
              </FormField>
              <Divider fullWidth={false} />
              <FormField>
                <Section
                  heading={productDetailHeadingText}
                  defaultExpand={true}
                  disableShowMore={true}
                >
                  <p>{product.productDescription}</p>
                </Section>
              </FormField>
              <Divider fullWidth={false} />
              <FormField>
                <Section
                  heading={shippingHeadingText(product.countryLabel)}
                  defaultExpand={true}
                  disableShowMore={true}
                >
                  <BulletPointsDescription
                    bulletPoints={product.shippingInfo}
                  />
                  <Heading
                    text={buyerShippingText(product.countryLabel)}
                    Element="h6"
                  />
                </Section>
              </FormField>
              <Divider fullWidth={false} />
              <FormField>
                <Heading text={buyerDisclosure} Element="h6" />
              </FormField>
              <Divider fullWidth={false} />
              <FormField>
                <Heading text={sellYourOwnText} Element="h5" />
                <Button
                  type="secondary"
                  block
                  className={styles.createListing}
                  href="/instantcommerce/addproduct/?entry=listingReferral"
                  onClick={() =>
                    recordClick({
                      link: constants.fpti.link.createListingReferral,
                      item: buttonId,
                      ...rest,
                    })
                  }
                >
                  <Message id="checkout.referral.createListing.text" />
                </Button>
                <FormField>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: 'auto',
                    }}
                  >
                    <a
                      href={`https://www.${domain}/re/smarthelp/contact-us?email=paypalme`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {reportListing}
                    </a>
                  </div>
                </FormField>
              </FormField>
            </Form>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              height: 'auto',
            }}
          >
            <div>
              <Heading text={madeWith} Element="h6" centerAlign={true} />
            </div>
            <div>
              <Glyph icon="icon-pp-logo" />
            </div>
          </div>
          <Divider />
        </>
      )}
    </>
  )
}

Checkout.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
  }).isRequired,
}

export default applicationData(Checkout)
